import type { ExperimentStatus } from '@orderlymeds/measurements-schemas';
import { EXPERIMENT_STATUS_TRANSITIONS } from '@orderlymeds/measurements-schemas';
import { useParams } from 'react-router-dom';
import { useChangeExperimentStatus, useExperiment } from '../../api/experiments';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ChangelogTimeline } from '../../components/ChangelogTimeline';
import { PageHeader } from '../../components/PageHeader';
import { StatusIndicator } from '../../components/StatusIndicator';
import { Table } from '../../components/Table';
import { TargetingSummary } from '../../components/TargetingSummary';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import shared from '../shared.module.css';
import styles from './ExperimentDetailPage.module.css';

function statusButtonVariant(next: ExperimentStatus): 'primary' | 'secondary' {
	if (next === 'running') return 'primary';
	return 'secondary';
}

function statusButtonLabel(next: ExperimentStatus): string {
	switch (next) {
		case 'running':
			return 'Start';
		case 'paused':
			return 'Pause';
		case 'completed':
			return 'Complete';
		default:
			return next.charAt(0).toUpperCase() + next.slice(1);
	}
}

export function ExperimentDetailPage() {
	const { id, expId } = useParams<{ id: string; expId: string }>();
	const isAdmin = useIsAdmin();

	const { data: experiment, isLoading, error } = useExperiment(id ?? '', expId ?? '');
	const changeStatus = useChangeExperimentStatus(id ?? '', expId ?? '');
	const listBreadcrumbs = useProjectBreadcrumbs('Experiments');
	const detailBreadcrumbs = useProjectBreadcrumbs('Experiments', experiment?.name ?? '');

	async function handleStatusChange(next: ExperimentStatus) {
		await changeStatus.mutateAsync(next);
	}

	if (isLoading) {
		return (
			<div>
				<PageHeader title="Loading…" breadcrumbs={listBreadcrumbs} />
			</div>
		);
	}

	if (error || !experiment) {
		return (
			<div>
				<PageHeader title="Experiment" breadcrumbs={listBreadcrumbs} />
				<Card>
					<p className={shared.errorMessage}>
						{error instanceof Error ? error.message : 'Experiment not found'}
					</p>
				</Card>
			</div>
		);
	}

	const nextStatuses = EXPERIMENT_STATUS_TRANSITIONS[experiment.status] ?? [];

	const variantColumns = [
		{ key: 'id', label: 'ID', mono: true },
		{ key: 'name', label: 'Name' },
		{ key: 'weight', label: 'Weight %', mono: true },
	];

	return (
		<div>
			<PageHeader
				title={experiment.name}
				breadcrumbs={detailBreadcrumbs}
				actions={
					<div className={styles.headerActions}>
						<StatusIndicator status={experiment.status} />
						{isAdmin &&
							nextStatuses.map((next) => (
								<Button
									key={next}
									variant={statusButtonVariant(next)}
									onClick={() => handleStatusChange(next)}
									loading={changeStatus.isPending}
								>
									{statusButtonLabel(next)}
								</Button>
							))}
					</div>
				}
			/>

			<div className={shared.content}>
				{changeStatus.error && (
					<p className={shared.mutationError}>
						{changeStatus.error instanceof Error
							? changeStatus.error.message
							: 'Failed to change status'}
					</p>
				)}

				<Card active={experiment.status === 'running'}>
					<h2 className={shared.cardHeading}>Details</h2>
					<dl className={shared.detailList}>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Status</dt>
							<dd className={shared.detailValue}>
								<StatusIndicator status={experiment.status} />
							</dd>
						</div>
						{experiment.description && (
							<div className={shared.detailRow}>
								<dt className={shared.detailLabel}>Description</dt>
								<dd className={shared.detailValue}>{experiment.description}</dd>
							</div>
						)}
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Created by</dt>
							<dd className={[styles.detailValue, shared.mono].join(' ')}>
								{experiment.created_by}
							</dd>
						</div>
					</dl>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Variants</h2>
					<Table>
						<Table.Header columns={variantColumns} />
						<Table.Body>
							{experiment.variants.map((variant) => (
								<Table.Row key={variant.id}>
									<Table.Cell mono>{variant.id}</Table.Cell>
									<Table.Cell>{variant.name}</Table.Cell>
									<Table.Cell mono>{((variant.weight / 10000) * 100).toFixed(1)}%</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Targeting</h2>
					<TargetingSummary
						targeting={experiment.targeting}
						emptyMessage="No targeting rules — all visitors are eligible."
					/>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Activity</h2>
					<ChangelogTimeline
						projectId={id ?? ''}
						resourceType="experiment"
						resourceId={expId ?? ''}
					/>
				</Card>
			</div>
		</div>
	);
}
